import { ActionButton } from '@/components/common/ActionButton';
import { SECTION_LABEL } from '@/components/common/surfaces';
import { TOOL_MANAGEMENT_DIALOG_PT } from '@/components/dialogs/dialogTheme';
import type { ToolImportReport } from '@/types/toolImport.types';
import { Dialog } from 'primereact/dialog';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

/** Report fields that are plain counts, in the order they are shown. */
type CountField =
  | 'materials'
  | 'holders'
  | 'extensions'
  | 'tools'
  | 'cutting_data'
  | 'assemblies'
  | 'retired_assemblies';

const COUNT_FIELDS: { field: CountField; labelKey: string }[] = [
  { field: 'materials', labelKey: 'toolManagement.importCountMaterials' },
  { field: 'holders', labelKey: 'toolManagement.importCountHolders' },
  { field: 'extensions', labelKey: 'toolManagement.importCountExtensions' },
  { field: 'tools', labelKey: 'toolManagement.importCountTools' },
  { field: 'cutting_data', labelKey: 'toolManagement.importCountCuttingData' },
  { field: 'assemblies', labelKey: 'toolManagement.importCountAssemblies' },
  { field: 'retired_assemblies', labelKey: 'toolManagement.importCountRetiredAssemblies' },
];

interface ReportListProps {
  title: string;
  items: string[];
  /** Warnings can run to dozens of lines, so that one section scrolls instead of growing. */
  scrollable?: boolean;
}

/** One findings section; renders nothing when the backend reported no entries. */
const ReportList: FC<ReportListProps> = ({ title, items, scrollable = false }) => {
  if (items.length === 0) return null;

  return (
    <section className="flex flex-col gap-2">
      <h3 className={SECTION_LABEL}>
        {title} <span className="tabular-nums">({items.length})</span>
      </h3>
      <ul
        className={`m-0 list-disc rounded-lg bg-gray-50 p-4 pl-8 text-xs leading-relaxed text-gray-600 ${
          scrollable ? 'max-h-48 overflow-y-auto' : ''
        }`}
      >
        {items.map((item) => (
          <li key={item} className="break-words">
            {item}
          </li>
        ))}
      </ul>
    </section>
  );
};

interface ToolImportReportDialogProps {
  /** Report of the last successful import; null before the first one. */
  report: ToolImportReport | null;
  visible: boolean;
  onHide: () => void;
}

/**
 * What one hyperMILL import actually did: the row counts first, then the findings.
 *
 * The findings sections are the reason this is a dialog rather than a toast — an import
 * can succeed and still skip tools or leave holders unreferenced, and that is exactly
 * what the user needs to read before trusting the catalogue.
 */
export const ToolImportReportDialog: FC<ToolImportReportDialogProps> = ({ report, visible, onHide }) => {
  const { t } = useTranslation();

  return (
    <Dialog
      header={t('toolManagement.importReportTitle')}
      visible={visible}
      onHide={onHide}
      modal={true}
      className="w-full max-w-2xl"
      pt={TOOL_MANAGEMENT_DIALOG_PT}
      footer={
        <div className="flex justify-end gap-2">
          <ActionButton variant="ghost" onClick={onHide}>
            {t('common.close')}
          </ActionButton>
        </div>
      }
    >
      {report && (
        <div className="flex flex-col gap-6 pt-1">
          {/* Counts as tiles rather than as a key-value table: seven numbers read faster as a
              grid of figures than as seven rows the eye has to track across. */}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {COUNT_FIELDS.map(({ field, labelKey }) => (
              <div key={field} className="flex flex-col gap-1 rounded-xl bg-gray-50 p-4">
                <span className="text-2xl font-semibold tabular-nums text-gray-900">{report[field]}</span>
                <span className="text-xs text-gray-500 [text-wrap:pretty]">{t(labelKey)}</span>
              </div>
            ))}
          </div>

          <ReportList title={t('toolManagement.importSkippedTools')} items={report.skipped_tools} />
          <ReportList title={t('toolManagement.importToolsWithoutAssembly')} items={report.tools_without_assembly} />
          <ReportList title={t('toolManagement.importUnreferencedHolders')} items={report.unreferenced_holders} />
          <ReportList title={t('toolManagement.importWarnings')} items={report.warnings} scrollable={true} />
        </div>
      )}
    </Dialog>
  );
};
